import { isArray, isObject } from 'lodash/fp';

import { BaseOption, Nullable, OptionOptional } from '@/types/generic';
import { LinkType } from '@/types/link';
import { NestedObject, NestedObjectValue } from '@/types/object';
import { isNullish } from './predicates';

interface Link {
  label: string;
  id: number;
  type: LinkType;
}

export const addLink = (object: NestedObject, label: string, id: number, linkType: LinkType) => ({
  ...object,
  [linkType]: {
    id,
    label,
  },
});

export const addLinks = (object: NestedObject, links: Link[]) =>
  links.reduce((obj, link) => addLink(obj, link.label, link.id, link.type), object);

/**
 * Function to flatten any object into a hyphen delimited reference
 */
export const flattenAndAddPrefix = (
  objectToFlatten: NestedObject | NestedObject[] | NestedObjectValue[] | NestedObject[][],
  delimiter: string = '-',
  removeEmptyValues: boolean = true,
): Record<string, NestedObjectValue> => {
  const output: { [key: string]: NestedObjectValue } = {};

  Object.entries(objectToFlatten).forEach(([objKey, objValue]) => {
    if (isNullish(objValue) && removeEmptyValues) return;

    if (typeof objValue === 'object' && !isNullish(objValue)) {
      const flatObject = flattenAndAddPrefix(objValue, delimiter, removeEmptyValues);

      Object.entries(flatObject)
        .filter(([_, flatObjValue]) => !isNullish(flatObjValue) || !removeEmptyValues)
        .forEach(([flatObjKey, flatObjValue]) => {
          output[objKey + delimiter + flatObjKey] = flatObjValue;
        });
    } else {
      output[objKey] = objValue;
    }
  });

  return output;
};

export const convertToDropdownOption = <T>(label: unknown, value: T, optional?: OptionOptional) => ({
  label: String(label),
  value,
  optional,
});

/**
 * Takes a key and a new value, and returns a new object based on the existingOptional but with the key replaced
 * if the new value is truthy and the existing value is falsy
 */
export const replaceOptionalKeyIfExistingValueFalsy = <K extends keyof OptionOptional>(
  key: K,
  existingOptional?: OptionOptional,
  newVal?: OptionOptional[K],
) => (newVal && !existingOptional?.[key] ? { ...existingOptional, [key]: newVal } : existingOptional);

export const optionsToValueStrings = <T>(options: Nullable<BaseOption<T>[]>) =>
  options?.map(({ value }) => `${value}`) || [];

export type ObjectValue = string | number | number[] | string[] | boolean | boolean[];

const isEmptyString = (value: ObjectValue): boolean => value === '';

const isNaNNumber = (value: ObjectValue): boolean => typeof value === 'number' && Number.isNaN(value);

const isEmptyArray = (value: ObjectValue): boolean => Array.isArray(value) && value.length === 0;

const hasValue = (value: Nullable<ObjectValue>): value is ObjectValue =>
  !isNullish(value) && !isEmptyString(value) && !isNaNNumber(value) && !isEmptyArray(value);

export const toObject = (key: Nullable<string>, value: Nullable<ObjectValue>): Record<string, ObjectValue> =>
  key && hasValue(value) ? { [key]: value } : {};

export const isEmptyObject = (data: unknown) => !isArray(data) && isObject(data) && !Object.keys(data).length;

export const isObjectWithEmptyValues = (data: unknown): boolean => {
  if (!isObject(data) || isArray(data)) return false;

  return Object.values(data).every(
    value => (isArray(value) && !value.length) || (isObject(value) && !Object.keys(value).length),
  );
};
